import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  UserPlus, Trash2, KeyRound, Ban, CheckCircle2, Copy, Mail,
  ShieldCheck, User as UserIcon, X, Unlock, Users as UsersIcon, Filter,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const inputCls =
  "w-full rounded-lg border border-slate-800 bg-slate-900 text-slate-100 placeholder:text-slate-500 px-3 py-2 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20";

export default function AdminUsers() {
  const { state } = useAuth();
  const meId = state?.user?.id;
  const [users, setUsers] = useState(null);
  const [partners, setPartners] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [resetInfo, setResetInfo] = useState(null); // { reset_url, expires_at, email, email_sent }
  const [tempCredsInfo, setTempCredsInfo] = useState(null); // { email, temp_password }
  // Partner filter — driven from the URL so AdminPartners can deep-link
  // to a specific partner's team ("N team" chip → /admin/users?partner=CODE)
  // and a shared link stays scoped to that partner. Special values:
  //   ""       → all users
  //   "admins" → admin-role users only (no partner_code)
  //   "<code>" → just users under that partner_code (owner + staff)
  const [searchParams, setSearchParams] = useSearchParams();
  const partnerFilter = searchParams.get("partner") || "";
  const setPartnerFilter = (v) => {
    const q = new URLSearchParams(searchParams);
    if (v) q.set("partner", v); else q.delete("partner");
    setSearchParams(q, { replace: true });
  };
  const visibleUsers = useMemo(() => {
    if (!users) return null;
    if (!partnerFilter) return users;
    if (partnerFilter === "admins") return users.filter((u) => u.role === "admin");
    return users.filter((u) => (u.partner_code || "") === partnerFilter);
  }, [users, partnerFilter]);
  // Per-partner team-size counts so the filter dropdown shows "(N)" pills
  // — makes it easy to spot which partners have grown a team vs which are
  // still solo.
  const teamCounts = useMemo(() => {
    const m = { admins: 0 };
    (users || []).forEach((u) => {
      if (u.role === "admin") m.admins = (m.admins || 0) + 1;
      else if (u.partner_code) m[u.partner_code] = (m[u.partner_code] || 0) + 1;
    });
    return m;
  }, [users]);

  async function reload() {
    try {
      const [u, p] = await Promise.all([
        api.get("/commissions/users"),
        api.get("/commissions/partners"),
      ]);
      setUsers(u.data.users || []);
      // /commissions/partners returns a raw array, not {partners:[]}
      setPartners(Array.isArray(p.data) ? p.data : (p.data.partners || []));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load users");
    }
  }
  useEffect(() => { reload(); }, []);

  async function toggleDisabled(u) {
    if (u.id === meId) {
      toast.error("You can't disable your own account.");
      return;
    }
    try {
      await api.patch(`/commissions/users/${u.id}`, { disabled: !u.disabled });
      toast.success(u.disabled ? "User re-enabled" : "User disabled");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    }
  }

  async function deleteUser(u) {
    if (u.id === meId) {
      toast.error("You can't delete your own account.");
      return;
    }
    if (!window.confirm(`Delete ${u.email}? This can't be undone.`)) return;
    try {
      await api.delete(`/commissions/users/${u.id}`);
      toast.success("User deleted");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  }

  async function issueReset(u) {
    try {
      const { data } = await api.post(`/commissions/users/${u.id}/reset-link`);
      setResetInfo(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reset link failed");
    }
  }

  async function unlockUser(u) {
    // Clear the (ip, email) lockout + attempt counters so a user who
    // hit "Too many failed attempts. Try again in 15 minutes." can
    // log in immediately with the correct password. Fixes the "two
    // computers can't both log in" symptom when one machine typed
    // the wrong password enough times to trip the 5-fail threshold.
    try {
      const { data } = await api.post(`/commissions/users/${u.id}/unlock`);
      if (data.lockouts_cleared > 0) {
        toast.success(`Cleared ${data.lockouts_cleared} lockout${data.lockouts_cleared === 1 ? "" : "s"} for ${u.email}`);
      } else {
        toast.info(`${u.email} isn't currently locked out.`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Unlock failed");
    }
  }

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl tracking-tight">Users</h1>
          <p className="text-sm text-slate-400 mt-1">
            Admins run the console. Partners see their own earnings, and partner owners can invite staff of their own — all listed here so you can reset a lockout or password for any of them.
          </p>
        </div>
        <button
          data-testid="users-create-button"
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-white px-4 py-2 rounded-lg text-sm font-medium"
        >
          <UserPlus className="w-4 h-4" /> Invite user
        </button>
      </div>

      {/* Partner filter — deep-linkable via ?partner=CODE. Shows a
          quick "clear" pill when a partner is actively selected so
          admins can jump back to the flat list in one click. */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="inline-flex items-center gap-2 text-xs text-slate-400">
          <Filter className="w-3.5 h-3.5" /> Filter by partner
        </div>
        <select
          data-testid="users-partner-filter"
          value={partnerFilter}
          onChange={(e) => setPartnerFilter(e.target.value)}
          className="rounded-lg border border-slate-800 bg-slate-900 text-slate-100 text-sm px-3 py-1.5 focus:border-cyan-500 outline-none"
        >
          <option value="">All users ({(users || []).length})</option>
          <option value="admins">Admins only ({teamCounts.admins || 0})</option>
          <option disabled>──────────</option>
          {partners.map((p) => (
            <option key={p.code} value={p.code}>
              {p.name} ({p.code}) — {teamCounts[p.code] || 0}
            </option>
          ))}
        </select>
        {partnerFilter && (
          <button
            type="button"
            data-testid="users-partner-filter-clear"
            onClick={() => setPartnerFilter("")}
            className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-200 px-2 py-1 rounded-md hover:bg-slate-800"
          >
            <X className="w-3 h-3" /> Clear filter
          </button>
        )}
        {partnerFilter && partnerFilter !== "admins" && (
          <span className="inline-flex items-center gap-1.5 text-[11px] text-cyan-300 bg-cyan-500/10 border border-cyan-500/30 px-2 py-0.5 rounded-full font-mono">
            <UsersIcon className="w-3 h-3" /> {partnerFilter} team
          </span>
        )}
      </div>

      <div className="rounded-xl border border-slate-800 overflow-hidden">
        {visibleUsers === null ? (
          <div className="p-6 text-sm text-slate-500">Loading…</div>
        ) : visibleUsers.length === 0 ? (
          <div className="p-6 text-sm text-slate-500">
            {partnerFilter
              ? "No users under this filter — the partner hasn't invited anyone yet, or their team lives under a different code."
              : "No users yet."}
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="users-table">
            <thead className="bg-slate-950/60 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">User</th>
                <th className="text-left px-4 py-3">Role</th>
                <th className="text-left px-4 py-3">Partner</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {visibleUsers.map((u) => (
                <tr key={u.id} className="hover:bg-slate-950/40" data-testid={`user-row-${u.email}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-slate-800 grid place-items-center text-slate-300">
                        {u.role === "admin" ? <ShieldCheck className="w-4 h-4" /> : <UserIcon className="w-4 h-4" />}
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium text-slate-100 truncate">
                          {u.name || u.email.split("@")[0]}
                          {u.id === meId ? <span className="ml-2 text-[10px] uppercase tracking-wider text-cyan-400">You</span> : null}
                        </div>
                        <div className="text-xs text-slate-500 truncate">{u.email}</div>
                        {u.role === "partner_staff" && u.invited_by ? (
                          <div className="text-[10px] text-slate-500 mt-0.5 truncate" title={`Invited by ${u.invited_by}`}>
                            invited by <span className="text-slate-400">{u.invited_by}</span>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${
                      u.role === "admin"
                        ? "bg-cyan-500/15 text-cyan-300 border border-cyan-500/30"
                        : u.role === "partner"
                        ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                        : u.role === "partner_staff"
                        ? "bg-violet-500/15 text-violet-200 border border-violet-500/30"
                        : "bg-slate-800 text-slate-200 border border-slate-700"
                    }`}>
                      {u.role === "partner" ? "partner (owner)" : u.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-400 font-mono text-xs">
                    {u.partner_code || "—"}
                  </td>
                  <td className="px-4 py-3">
                    {u.disabled ? (
                      <span className="inline-flex items-center gap-1 text-xs text-amber-300">
                        <Ban className="w-3 h-3" /> Disabled
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-300">
                        <CheckCircle2 className="w-3 h-3" /> Active
                      </span>
                    )}
                    {u.must_change_password ? (
                      <div className="text-[10px] text-slate-500 mt-0.5">Must change password on next login</div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      <button
                        data-testid={`user-reset-${u.email}`}
                        title="Issue password reset link"
                        onClick={() => issueReset(u)}
                        className="p-2 rounded-lg text-slate-400 hover:text-cyan-300 hover:bg-slate-800"
                      >
                        <KeyRound className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`user-unlock-${u.email}`}
                        title="Clear login lockouts (Too many failed attempts)"
                        onClick={() => unlockUser(u)}
                        className="p-2 rounded-lg text-slate-400 hover:text-emerald-300 hover:bg-slate-800"
                      >
                        <Unlock className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`user-toggle-${u.email}`}
                        title={u.disabled ? "Re-enable user" : "Disable user"}
                        disabled={u.id === meId}
                        onClick={() => toggleDisabled(u)}
                        className="p-2 rounded-lg text-slate-400 hover:text-amber-300 hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <Ban className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`user-delete-${u.email}`}
                        title="Delete user"
                        disabled={u.id === meId}
                        onClick={() => deleteUser(u)}
                        className="p-2 rounded-lg text-slate-400 hover:text-red-400 hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate && (
        <CreateUserModal
          partners={partners}
          onClose={() => setShowCreate(false)}
          onCreated={(created) => {
            setShowCreate(false);
            reload();
            if (created?.temp_password) {
              setTempCredsInfo({ email: created.email, temp_password: created.temp_password });
            }
          }}
        />
      )}

      {resetInfo && (
        <ResetLinkModal info={resetInfo} onClose={() => setResetInfo(null)} />
      )}
      {tempCredsInfo && (
        <TempCredsModal info={tempCredsInfo} onClose={() => setTempCredsInfo(null)} />
      )}
    </div>
  );
}

function CreateUserModal({ partners, onClose, onCreated }) {
  const [form, setForm] = useState({
    email: "", name: "", role: "admin", partner_code: "", password: "",
  });
  const [busy, setBusy] = useState(false);
  const [autoPw, setAutoPw] = useState(true);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {
        email: form.email.trim(),
        name: form.name.trim() || null,
        role: form.role,
        partner_code: form.role === "partner" ? form.partner_code : null,
        password: autoPw ? null : form.password,
      };
      const { data } = await api.post("/commissions/users", body);
      toast.success("User created");
      onCreated(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Create failed");
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl border border-slate-800 bg-slate-900 p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-display text-xl tracking-tight">Invite a new user</h3>
            <p className="text-sm text-slate-400 mt-0.5">They&apos;ll sign in at the same login page.</p>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-100"><X className="w-5 h-5"/></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <label className="block col-span-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Email</span>
              <input
                data-testid="create-user-email"
                required type="email" value={form.email}
                onChange={(e) => setForm({...form, email: e.target.value})}
                className={inputCls} placeholder="person@company.com"
              />
            </label>
            <label className="block col-span-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Full name (optional)</span>
              <input
                data-testid="create-user-name"
                value={form.name}
                onChange={(e) => setForm({...form, name: e.target.value})}
                className={inputCls} placeholder="Jane Doe"
              />
            </label>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Role</span>
              <select
                data-testid="create-user-role"
                value={form.role}
                onChange={(e) => setForm({...form, role: e.target.value})}
                className={inputCls}
              >
                <option value="admin">Admin</option>
                <option value="partner">Partner</option>
              </select>
            </label>
            {form.role === "partner" && (
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Partner code</span>
                <select
                  data-testid="create-user-partner-code"
                  value={form.partner_code}
                  onChange={(e) => setForm({...form, partner_code: e.target.value})}
                  className={inputCls}
                  required
                >
                  <option value="">Select…</option>
                  {partners.map((p) => (
                    <option key={p.code} value={p.code}>{p.name} ({p.code})</option>
                  ))}
                </select>
              </label>
            )}
          </div>

          <div className="rounded-lg border border-slate-800 bg-slate-950/40 p-3">
            <label className="flex items-start gap-2 text-sm text-slate-300 cursor-pointer">
              <input
                data-testid="create-user-auto-pw"
                type="checkbox" checked={autoPw}
                onChange={(e) => setAutoPw(e.target.checked)}
                className="mt-1"
              />
              <span>
                <span className="font-medium text-slate-200">Auto-generate a temporary password</span>
                <span className="block text-xs text-slate-500 mt-0.5">You&apos;ll see it once — copy it and share it via a channel the user trusts.</span>
              </span>
            </label>
            {!autoPw && (
              <label className="block mt-3">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Password</span>
                <input
                  data-testid="create-user-password"
                  type="password" value={form.password}
                  onChange={(e) => setForm({...form, password: e.target.value})}
                  className={inputCls + " font-mono"}
                  placeholder="Min 10 chars, upper, lower, digit"
                />
              </label>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-800 hover:bg-slate-800 text-sm">
              Cancel
            </button>
            <button
              data-testid="create-user-submit"
              type="submit" disabled={busy}
              className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-60"
            >
              {busy ? "Creating…" : "Create user"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ResetLinkModal({ info, onClose }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(info.reset_url);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch (_) { toast.error("Copy failed"); }
  }
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl border border-slate-800 bg-slate-900 p-6" onClick={(e) => e.stopPropagation()} data-testid="reset-link-modal">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-display text-xl tracking-tight">Password reset link</h3>
            <p className="text-sm text-slate-400 mt-0.5">
              For <span className="text-slate-200">{info.email}</span> · expires in 30 min
            </p>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-100"><X className="w-5 h-5"/></button>
        </div>
        <div className="rounded-lg border border-slate-800 bg-slate-950 p-3 font-mono text-xs text-slate-300 break-all" data-testid="reset-link-url">
          {info.reset_url}
        </div>
        <div className="flex items-center gap-2 mt-3">
          {info.email_sent ? (
            <span className="inline-flex items-center gap-1.5 text-xs text-emerald-300">
              <Mail className="w-3.5 h-3.5" /> Also emailed to {info.email}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-xs text-amber-300">
              <Mail className="w-3.5 h-3.5" /> Not emailed — SMTP isn&apos;t configured yet. Share this link out-of-band.
            </span>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-800 hover:bg-slate-800 text-sm">
            Close
          </button>
          <button
            data-testid="reset-link-copy"
            onClick={copy}
            className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-white px-4 py-2 rounded-lg text-sm font-medium"
          >
            {copied ? <><CheckCircle2 className="w-4 h-4"/> Copied</> : <><Copy className="w-4 h-4"/> Copy link</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function TempCredsModal({ info, onClose }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(`Email: ${info.email}\nTemporary password: ${info.temp_password}`);
      setCopied(true);
      toast.success("Credentials copied");
      setTimeout(() => setCopied(false), 2000);
    } catch (_) { toast.error("Copy failed"); }
  }
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl border border-slate-800 bg-slate-900 p-6" onClick={(e) => e.stopPropagation()} data-testid="temp-creds-modal">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-display text-xl tracking-tight">Temporary password</h3>
            <p className="text-sm text-amber-300 mt-1">Shown once — copy it before closing.</p>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-100"><X className="w-5 h-5"/></button>
        </div>
        <div className="rounded-lg border border-slate-800 bg-slate-950 p-4 space-y-2">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Email</div>
            <div className="font-mono text-sm text-slate-200">{info.email}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Temporary password</div>
            <div className="font-mono text-sm text-slate-200" data-testid="temp-creds-password">{info.temp_password}</div>
          </div>
        </div>
        <p className="text-xs text-slate-500 mt-3">
          The user will be required to change this on their next successful login.
        </p>
        <div className="flex justify-end gap-2 mt-4">
          <button
            data-testid="temp-creds-copy"
            onClick={copy}
            className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-white px-4 py-2 rounded-lg text-sm font-medium"
          >
            {copied ? <><CheckCircle2 className="w-4 h-4"/> Copied</> : <><Copy className="w-4 h-4"/> Copy both</>}
          </button>
        </div>
      </div>
    </div>
  );
}
